import { Direction, Order, Query, Result } from '@rivethealth/data/super-table';
import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { Subject } from 'rxjs/internal/Subject';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  scan,
  skip,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { useInit, useSubscriptionLatest } from '../../common/core/hooks';
import { Loadable, loadMap, loadPipe } from '../helpers/loadable';
import { combineLatestObject, replayLast } from '../rx/util';
import { NumberParam, StringParam } from '../state/route';
import { RouteService, useQueryParam } from '../state/route.service';
import { DataSource } from './data-source';
import { ViewState } from './super-table.component';

/**
 * Manage filter, search, and paging state for a super table.
 */
export function listManager<Filter>(
  routeService: RouteService,
  options: {
    page: number;
    pageSize: number;
    filter: Filter;
    setFilter: (filter: Partial<Filter>) => void;
    order: Order;
    tableName?: string; // Optional tableName parameter
  },
): AltListManager<Filter> {
  // Prefix for query parameters based on the optional tableName
  const queryPrefix = options.tableName ? `${options.tableName}_` : '';

  const [page, setPage] = useQueryParam(
    routeService,
    new NumberParam(`${queryPrefix}page`, options.page),
  );
  const [pageSize, setPageSize] = useQueryParam(
    routeService,
    new NumberParam(`${queryPrefix}pageSize`, options.pageSize),
  );
  const [search, setSearchFn] = useQueryParam(
    routeService,
    new StringParam('search'),
  );
  const [order, setOrder] = useQueryParam(
    routeService,
    new StringParam(
      'order',
      `${options.order.name}__${options.order.direction}`,
    ),
  );
  const setSearch = (text: string) => {
    setSearchFn(text), setPage(0);
  };
  const setFilter = (filter: Partial<Filter>) => {
    options.setFilter(filter);
    setPage(0);
  };

  const setSort = (sortInput: Order) => {
    setOrder(`${sortInput.name}__${sortInput.direction}`);
  };

  const setTableView = (view: ViewState) => {
    setPage(view.page);
    setPageSize(view.pageSize);
  };

  const [sortName, sortDirection] = order ? order.split('__') : '';
  const sort =
    sortName != '' && sortDirection != ''
      ? { name: sortName, direction: sortDirection as Direction }
      : options.order;

  const tableView: ViewState = {
    page: page || 0,
    pageSize: pageSize || options.pageSize,
    sortDisabled: false,
  };

  const query: Query<Filter, string> = {
    filters: options.filter,
    range:
      pageSize == null
        ? null
        : {
            count: pageSize,
            start: (page || 0) * pageSize,
          },
    search: search || '',
    sort,
  };

  const refreshSubject = useInit(() => new BehaviorSubject(null));

  const refreshCount =
    useSubscriptionLatest(refreshSubject.pipe(scan(acc => acc + 1, 0)), [
      refreshSubject,
    ]) ?? 0;

  const load = <T, N>(dataSource: DataSource<T, Filter, N>) =>
    combineLatest([of(query), refreshSubject]).pipe(
      switchMap(([query]) => dataSource.query(query).pipe(loadPipe())),
    );

  return {
    filter: options.filter,
    search,
    sort,
    tableView,
    refresh: () => refreshSubject.next(null),
    refreshCount,
    setTableView,
    setPage,
    setSearch,
    load,
    setFilter,
    setSort,
  };
}

export function getAltListManagerQuery<Filter>(
  listManager: AltListManager<Filter>,
): Query<Filter, string> {
  return {
    filters: listManager.filter,
    range:
      listManager.tableView.pageSize == null
        ? null
        : {
            count: listManager.tableView.pageSize,
            start: listManager.tableView.page * listManager.tableView.pageSize,
          },
    search: listManager.search || '',
    sort: listManager.tableView.sortDisabled
      ? { name: 'default', direction: 'asc' }
      : listManager.sort,
  };
}

export class ListManager<Filter = {}> {
  private readonly options: ListStateOptions<Filter> = {
    ...(<any>SuperTableOptions.defaults()),
    ...(<any>this.explicitOptions),
  };

  constructor(
    private readonly explicitOptions: ListStateOptionsPartial<Filter>,
  ) {}

  readonly filter = this.options.filter;

  readonly sort = this.options.sort;

  readonly search = <Subject<string>>this.options.search;

  readonly page = this.options.page;

  readonly pageSize = this.options.pageSize;

  readonly sortDisabled = new BehaviorSubject(false);

  private readonly debounceSearch =
    this.options.searchDebounce == null
      ? this.search.pipe(replayLast())
      : this.search.pipe(
          debounceTime(this.options.searchDebounce),
          distinctUntilChanged(),
          replayLast(),
        );

  readonly viewSideEffects: Observable<Partial<ViewState>> = merge(
    this.filter.pipe(
      skip(1),
      map(() => {
        return { page: 0, sortDisabled: false };
      }),
    ),
    this.debounceSearch.pipe(
      skip(1),
      map(search => ({ page: 0, sortDisabled: false })),
    ),
  ).pipe(
    tap(({ page, sortDisabled }) => {
      this.page.next(page);
      this.sortDisabled.next(sortDisabled);
    }),
  );

  readonly tableView: Subject<ViewState> = combineLatestObject({
    page: <Subject<number>>this.page.pipe(map(p => p || 0)),
    pageSize: <Subject<number>>(
      this.pageSize.pipe(map(p => p || this.options.defaultPageSize))
    ),
    sortDisabled: this.sortDisabled,
  });

  readonly refresh = new BehaviorSubject(null);

  readonly query: Observable<Query<Filter, string>> = combineLatest([
    this.filter,
    this.sort,
    this.tableView,
    this.debounceSearch.pipe(startWith('')),
    this.refresh,
  ]).pipe(
    switchMap(value => Promise.resolve(value)),
    map(([filter, sort, tableView, search]) => ({
      filters: filter,
      range:
        tableView.pageSize == null
          ? null
          : {
              count: tableView.pageSize,
              start: tableView.page * tableView.pageSize,
            },
      search,
      sort: tableView.sortDisabled
        ? { name: 'default', direction: 'asc' }
        : sort,
    })),
  );

  load<T, N = null>(
    dataSource: DataSource<T, Filter, N>,
  ): Observable<Loadable<Result<T, N>>> {
    return this.query.pipe(loadMap(query => dataSource.query(query)));
  }

  allPages<T, N = null>(
    dataSource: DataSource<T, Filter, N>,
    filters?: Filter | undefined,
  ): Observable<Loadable<Result<T, N>>> {
    return this.options.filter.pipe(
      withLatestFrom(this.sort, this.tableView, this.debounceSearch),
      debounceTime(1),
      loadMap(([filter, sort, tableView, search]) =>
        dataSource.query({
          filters: filter,
          range: null,
          search,
          sort: tableView.sortDisabled
            ? { name: 'default', direction: 'asc' }
            : sort,
        }),
      ),
    );
  }

  // only use as a last resort; possible performance implications
  altListManagerProxy: Observable<AltListManager<Filter>> = combineLatest([
    this.filter,
    this.search,
    this.sort,
    this.tableView,
  ]).pipe(
    map(([filter, search, sort, tableView]) => ({
      filter,
      search,
      sort,
      tableView,
      refresh: () => this.refresh.next(null),
      refreshCount: 0, // TODO: should we make this work for old ListManagers?
      setTableView: (view: ViewState) => {
        this.tableView.next(view);
        this.page.next(view.page);
      },
      setPage: (value: number | null) => this.page.next(value),
      setSearch: (text: string) => this.search.next(text),
      load: this.load.bind(this),
      setFilter: (f: Partial<Filter>) => this.filter.next({ ...filter, ...f }),
      setSort: (sortInput: Order) => this.sort.next(sortInput),
    })),
  );
}

export interface ListStateOptionsDefault {
  /**
   * Default sort (defaults to 'default', 'asc').
   * Does not work with `LocalDataSource`s for unknown reasons. For those, use
   * a sorter named 'default' instead.
   */
  defaultSort: Order;
  /**
   * Default page size (defaults to 25).
   */
  defaultPageSize: number | null;
  /**
   * Search
   */
  search: Subject<string>;
  /**
   * Search debounce time, or null for no debounce (defaults to 1ms).
   */
  searchDebounce: number | null;
}

export interface ListStateOptionsFilter<Filter> {
  /**
   * Filters (defaults to empty).
   */
  filter: Subject<Filter>;
}

export interface ListStateOptionsOrder {
  /**
   * Sort order (defaults to defaultSort).
   */
  sort: Subject<Order>;
}

export interface ListStateViewPage {
  /**
   * set the page number and page size
   * default page size to 250
   */
  page: Subject<number | null>;
  pageSize: Subject<number | null>;
}

export type ListStateOptions<Filter> = ListStateOptionsDefault &
  ListStateOptionsFilter<Filter> &
  ListStateOptionsOrder &
  ListStateViewPage;

export type ListStateOptionsPartial<Filter> = {} extends Filter
  ? Partial<
      ListStateOptionsFilter<Filter> &
        ListStateOptionsDefault &
        ListStateOptionsOrder &
        ListStateViewPage
    >
  : ListStateOptionsFilter<Filter> &
      Partial<
        ListStateOptionsDefault & ListStateOptionsOrder & ListStateViewPage
      >;

export type AltListManager<Filters = {}> = {
  filter: Filters;
  search: string | null;
  sort: Order;
  tableView: ViewState;
  refresh: () => void;
  refreshCount: number;
  setTableView: (view: ViewState) => void;
  setPage: (value: number | null) => void;
  setSearch: (text: string) => void;
  load: <T, N>(
    dataSource: DataSource<T, Filters, N>,
  ) => Observable<Loadable<Result<T, N>>>;
  setFilter: (filters: Partial<Filters>) => void;
  setSort: (sortInput: Order) => void;
};

export namespace SuperTableOptions {
  export function defaults(): ListStateOptionsDefault &
    ListStateOptionsFilter<{}> &
    ListStateOptionsOrder &
    ListStateViewPage {
    return {
      defaultSort: { name: 'default', direction: 'asc' },
      defaultPageSize: 250,
      page: new BehaviorSubject<number | null>(0),
      pageSize: new BehaviorSubject<number | null>(null),
      filter: new BehaviorSubject({}),
      sort: new BehaviorSubject(<Order>{ name: 'default', direction: 'asc' }),
      search: new BehaviorSubject(''),
      searchDebounce: 1,
    };
  }
}
